import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import type { PromotionChange, PromotionRequest, MfeVersion, EnvironmentsResponse, ProductsResponse } from "../types";
import { filterMfeNames } from "../product-utils";
import {
  getSignificantlyOutdatedMfes,
  getSignificantlyOutdatedEntries,
  SIGNIFICANT_VERSION_GAP,
  type PromotionSourceEnvironment,
} from "./environment-overview.logic";

@customElement("diff-preview-dialog")
export class DiffPreviewDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }

    .table-wrap {
      max-height: min(60vh, 640px);
      overflow: auto;
    }

    th {
      text-align: left;
      padding: var(--spacing-8, 8px);
      border-bottom: 2px solid var(--colors-border-border-gray-300, #e0e3e8);
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    td {
      padding: var(--spacing-8, 8px);
      border-bottom: 1px solid var(--colors-border-border-gray-300, #e0e3e8);
    }

    td.mono {
      font-family: monospace;
      font-size: 12px;
    }

    .arrow {
      color: var(--colors-foreground-fg-brand-600, #4a90d9);
      font-weight: 600;
    }

    .selection-bar {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      margin-bottom: var(--spacing-12, 12px);
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .deselected td {
      opacity: 0.4;
    }

    .snapshot-note {
      margin-bottom: var(--spacing-12, 12px);
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .version-select {
      font-family: monospace;
      font-size: 12px;
      min-width: 10ch;
      max-width: 16ch;
    }

    .version-select::part(combobox) {
      font-family: monospace;
      font-size: 12px;
      min-height: 28px;
      padding: 2px 6px;
    }

    .version-overridden::part(combobox) {
      color: var(--colors-foreground-fg-brand-600, #4a90d9);
      font-weight: 600;
    }

    .version-loading {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-style: italic;
    }

    .category-header td {
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      background: var(--colors-background-bg-secondary, #f9fafb);
      padding: var(--spacing-4, 4px) var(--spacing-8, 8px);
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
      width: 100%;
    }

    .omitted-warning {
      margin-bottom: var(--spacing-8, 8px);
      font-size: 12px;
    }

    .omitted-warning bee-card {
      padding: var(--spacing-12, 12px) var(--spacing-16, 16px);
    }

    .confirmation-body {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-16, 16px);
    }

    .confirmation-link {
      font-size: 13px;
      word-break: break-all;
    }
  `;

  @property({ type: Array }) changes: PromotionChange[] = [];
  @property({ attribute: false }) request: PromotionRequest = {
    source: "dev",
    target: "stage",
    mfes: [],
  };
  @property({ attribute: false }) environmentData: EnvironmentsResponse | null = null;
  @property({ attribute: false }) products: ProductsResponse | null = null;

  @state() private _applying = false;
  @state() private _error = "";
  @state() private _deselected: Set<string> = new Set();
  @state() private _versionOverrides: Map<string, string> = new Map();
  @state() private _availableVersions: Map<string, string[]> = new Map();
  @state() private _loadingVersions: Set<string> = new Set();
  @state() private _showPrerelease = false;
  @state() private _prResult: { prUrl: string; prNumber: number } | null = null;
  private _fetchGeneration = 0;

  willUpdate(changed: PropertyValues) {
    if (!changed.has("changes") && !changed.has("request")) return;
    if (!this.request.sourceVersions) return this._deselected = new Set();

    this._deselected = new Set(
      this.changes.filter((c) => !c.currentTargetVersion).map((c) => c.mfe),
    );
  }

  private _close = () => {
    this.dispatchEvent(new CustomEvent("close"));
  };

  private async _fetchVersions(mfeName: string) {
    if (this._availableVersions.has(mfeName) || this._loadingVersions.has(mfeName)) return;

    const generation = this._fetchGeneration;
    this._loadingVersions = new Set([...this._loadingVersions, mfeName]);
    try {
      const { versions } = await api.getMfeVersions(mfeName, this._showPrerelease);
      if (generation !== this._fetchGeneration) return;
      this._availableVersions = new Map([...this._availableVersions, [mfeName, versions]]);
    } catch {
      if (generation !== this._fetchGeneration) return;
      this._availableVersions = new Map([...this._availableVersions, [mfeName, []]]);
    } finally {
      if (generation === this._fetchGeneration) {
        const next = new Set(this._loadingVersions);
        next.delete(mfeName);
        this._loadingVersions = next;
      }
    }
  }

  private _togglePrerelease() {
    this._showPrerelease = !this._showPrerelease;
    this._fetchGeneration++;
    this._availableVersions = new Map();
    this._loadingVersions = new Set();
  }

  private _handleVersionChange(mfeName: string, version: string, originalVersion: string) {
    const next = new Map(this._versionOverrides);
    if (version === originalVersion) {
      next.delete(mfeName);
    } else {
      next.set(mfeName, version);
    }
    this._versionOverrides = next;
  }

  /**
   * Compare two semver strings for ordering (ascending: a < b returns negative).
   * Handles pre-release suffixes: release sorts after pre-release for the same base.
   */
  private _compareSemver(a: string, b: string): number {
    const [aBase, aPre] = a.split("-", 2);
    const [bBase, bPre] = b.split("-", 2);
    const pa = aBase.split(".").map(Number);
    const pb = bBase.split(".").map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const na = pa[i] ?? 0;
      const nb = pb[i] ?? 0;
      if (na !== nb) return na - nb;
    }
    if (!aPre && bPre) return 1;
    if (aPre && !bPre) return -1;
    if (aPre && bPre) return aPre.localeCompare(bPre);
    return 0;
  }

  /** Filter versions to only those <= the source environment's version. */
  private _capVersions(versions: string[], sourceVersion: string): string[] {
    return versions.filter((v) => this._compareSemver(v, sourceVersion) <= 0);
  }

  private _getEffectiveVersion(change: PromotionChange): string {
    return this._versionOverrides.get(change.mfe) ?? change.newVersion;
  }

  private _buildSourceVersions(): Record<string, MfeVersion> | undefined {
    if (this._versionOverrides.size === 0 && !this.request.sourceVersions) return undefined;

    const selectedChanges = this.changes.filter((c) => !this._deselected.has(c.mfe));
    const versions: Record<string, MfeVersion> = {};

    for (const change of selectedChanges) {
      const resolved = this._getEffectiveVersion(change);
      const major = resolved.split(".")[0];

      if (this.request.sourceVersions?.[change.mfe]) {
        versions[change.mfe] = this._versionOverrides.has(change.mfe)
          ? { major, resolved }
          : this.request.sourceVersions[change.mfe];
      } else {
        versions[change.mfe] = { major, resolved };
      }
    }

    return versions;
  }

  private _confirm = async () => {
    this._applying = true;
    this._error = "";
    try {
      const selectedChanges = this.changes.filter((c) => !this._deselected.has(c.mfe));
      const sourceVersions = this._buildSourceVersions();

      // Split selected changes by category
      const mfeChanges = selectedChanges.filter((c) => !c.category || c.category === "mfe");
      const shellChanges = selectedChanges.filter((c) => c.category === "shell");
      const servicesChanges = selectedChanges.filter((c) => c.category === "services");

      const result = await api.applyPromotion({
        ...this.request,
        mfes: mfeChanges.map((c) => c.mfe),
        ...(shellChanges.length > 0 ? { shell: shellChanges.map((c) => c.mfe) } : {}),
        ...(servicesChanges.length > 0 ? { services: servicesChanges.map((c) => c.mfe) } : {}),
        ...(sourceVersions ? { sourceVersions } : {}),
        ...(this._outdatedWarningText ? { outdatedWarning: this._outdatedWarningText } : {}),
      });

      this._prResult = { prUrl: result.prUrl, prNumber: result.prNumber };

      this.dispatchEvent(new CustomEvent("confirmed", {
        detail: { prUrl: result.prUrl, prNumber: result.prNumber },
      }));
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to create promotion PR";
    } finally {
      this._applying = false;
    }
  };

  private _isProdTarget(): boolean {
    return this.request.target === "prod";
  }

  private _formatSnapshotTimestamp(timestamp?: string): string | null {
    if (!timestamp) {
      return null;
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  private _toggleMfe(mfe: string) {
    const next = new Set(this._deselected);
    if (next.has(mfe)) {
      next.delete(mfe);
    } else {
      next.add(mfe);
    }
    this._deselected = next;
  }

  private _selectAll() {
    this._deselected = new Set();
  }

  private _isMissing(change: PromotionChange): boolean {
    return !change.currentTargetVersion;
  }

  private _deselectMissing() {
    const missing = this.changes
      .filter((c) => this._isMissing(c))
      .map((c) => c.mfe);
    this._deselected = new Set([...this._deselected, ...missing]);
  }

  private get _selectedCount(): number {
    return this.changes.length - this._deselected.size;
  }

  private get _hasMissing(): boolean {
    return this.changes.some((c) => this._isMissing(c));
  }

  private get _omittedOutdated(): { mfes: string[]; shell: string[]; services: string[] } | null {
    if (!this.environmentData) return null;
    const source = this.request.source;
    if (source === "prod") return null;

    const sourceEnv = source as PromotionSourceEnvironment;
    const selected = new Set(this.changes.filter((c) => !this._deselected.has(c.mfe)).map((c) => c.mfe));

    const allOutdatedMfes = getSignificantlyOutdatedMfes(this.environmentData, sourceEnv).filter((n) => !selected.has(n));
    const mfes = filterMfeNames(allOutdatedMfes, this.request.product ?? null, this.products);
    const shell = getSignificantlyOutdatedEntries(this.environmentData, "shell", sourceEnv).filter((n) => !selected.has(n));
    const services = getSignificantlyOutdatedEntries(this.environmentData, "services", sourceEnv).filter((n) => !selected.has(n));

    if (mfes.length + shell.length + services.length === 0) return null;
    return { mfes, shell, services };
  }

  private get _outdatedWarningText(): string {
    const omitted = this._omittedOutdated;
    if (!omitted) return "";

    const total = omitted.mfes.length + omitted.shell.length + omitted.services.length;
    const parts: string[] = [];
    if (omitted.mfes.length > 0) parts.push(omitted.mfes.join(", "));
    if (omitted.shell.length > 0) parts.push(omitted.shell.map((n) => `${n} (shell)`).join(", "));
    if (omitted.services.length > 0) parts.push(omitted.services.map((n) => `${n} (service)`).join(", "));

    return `${total} item${total > 1 ? "s" : ""} ${total > 1 ? "are" : "is"} ${SIGNIFICANT_VERSION_GAP}+ versions behind in ${this.request.target} but not included: ${parts.join(", ")}`;
  }

  render() {
    if (this._prResult) return this._renderConfirmation();
    return this._renderPreview();
  }

  private _renderConfirmation() {
    const isProdTarget = this._isProdTarget();
    const typeLabel = isProdTarget ? "Release" : "Promotion";

    return html`
      <bee-modal
        open
        in-root
        title="${typeLabel} PR Created"
        @bee-request-close=${this._close}
      >
        <div class="confirmation-body">
          <bee-alert variant="success" open>
            <span slot="message">${typeLabel} PR #${this._prResult!.prNumber} has been created.</span>
          </bee-alert>
          <div class="confirmation-link">
            <bee-link href=${this._prResult!.prUrl} target="_blank" rel="noopener noreferrer">${this._prResult!.prUrl}</bee-link>
          </div>
        </div>
        <div slot="footer" class="modal-footer">
          <bee-button label="Close" bee-type="secondary-grey" @click=${this._close}></bee-button>
          <bee-button label="Open PR" bee-type="primary" @click=${() => window.open(this._prResult!.prUrl, "_blank", "noopener,noreferrer")}></bee-button>
        </div>
      </bee-modal>
    `;
  }

  private _renderVersionCell(change: PromotionChange) {
    // Shell and services entries don't have R2 version listings — show static text
    if (change.category && change.category !== "mfe") {
      return html`<span>${change.newVersion}</span>`;
    }

    const mfe = change.mfe;
    const isLoading = this._loadingVersions.has(mfe);
    const versions = this._availableVersions.get(mfe);
    const overridden = this._versionOverrides.has(mfe);
    const effectiveVersion = this._getEffectiveVersion(change);

    // Cap to the source env's version — can't promote higher than what the source has.
    // When pre-release mode is on, skip the cap so newer pre-release builds are available.
    const cappedVersions = versions && versions.length > 0
      ? (this._showPrerelease ? versions : this._capVersions(versions, change.newVersion))
      : [];
    const allVersions = cappedVersions.length > 0
      ? (cappedVersions.includes(change.newVersion) ? cappedVersions : [change.newVersion, ...cappedVersions])
      : [change.newVersion];

    return html`
      <bee-select
        class="version-select ${overridden ? "version-overridden" : ""}"
        size="small"
        hoist
        .value=${effectiveVersion}
        ?disabled=${isLoading && !versions}
        @mouseenter=${() => this._fetchVersions(mfe)}
        @bee-show=${() => this._fetchVersions(mfe)}
        @bee-change=${(e: CustomEvent) => this._handleVersionChange(mfe, (e.target as HTMLSelectElement).value, change.newVersion)}
      >
        ${allVersions.map(
          (v) => {
            const label = v === change.newVersion ? `${v} (${this.request.source})` : v;
            return html`<bee-option value=${v}>${label}</bee-option>`;
          },
        )}
      </bee-select>
      ${isLoading && !versions ? html`<span class="version-loading">loading...</span>` : nothing}
    `;
  }

  private get _hasMultipleCategories(): boolean {
    const categories = new Set(this.changes.map((c) => c.category ?? "mfe"));
    return categories.size > 1;
  }

  private _groupedChanges(): Array<{ category: string; label: string; changes: PromotionChange[] }> {
    const groups: Array<{ category: string; label: string; changes: PromotionChange[] }> = [];
    const shellChanges = this.changes.filter((c) => c.category === "shell");
    const mfeChanges = this.changes.filter((c) => !c.category || c.category === "mfe");
    const servicesChanges = this.changes.filter((c) => c.category === "services");

    if (shellChanges.length > 0) groups.push({ category: "shell", label: "Shell", changes: shellChanges });
    if (mfeChanges.length > 0) groups.push({ category: "mfe", label: "MFEs", changes: mfeChanges });
    if (servicesChanges.length > 0) groups.push({ category: "services", label: "Services", changes: servicesChanges });

    return groups;
  }

  private _renderPreview() {
    const sourceLabel = this.request.sourceVersions ? `${this.request.source} snapshot` : this.request.source;
    const isProdTarget = this._isProdTarget();
    const confirmButtonType = isProdTarget ? "destructive" : "primary";
    const confirmLabel = isProdTarget ? "Create Release PR" : "Create Promotion PR";
    const snapshotTimestamp = this._formatSnapshotTimestamp(this.request.snapshotTimestamp);

    return html`
      <bee-modal
        open
        in-root
        title="Promote ${sourceLabel} to ${this.request.target}"
        type=${isProdTarget ? "error" : nothing}
        @bee-request-close=${this._close}
      >
        ${this.request.sourceVersions
          ? html`
              <div class="snapshot-note">
                Using saved snapshot values${this.request.snapshotName ? html` from ${this.request.snapshotName}` : nothing}${snapshotTimestamp ? html` captured ${snapshotTimestamp}` : nothing}, not the current live ${this.request.source} environment.
              </div>
            `
          : nothing}
        <div class="selection-bar">
          <span>${this._selectedCount} of ${this.changes.length} selected</span>
          <bee-button label="Select all" bee-type="tertiary" size="small" @click=${() => this._selectAll()} ?disabled=${this._deselected.size === 0}></bee-button>
          ${this._hasMissing
            ? html`<bee-button label="Deselect missing" bee-type="tertiary" size="small" @click=${() => this._deselectMissing()}></bee-button>`
            : nothing}
          <bee-toggle size="small" .checked=${this._showPrerelease} @bee-change=${() => this._togglePrerelease()}>Show pre-release</bee-toggle>
        </div>
        ${this._outdatedWarningText ? html`
          <div class="omitted-warning">
            <bee-card type="warning">${this._outdatedWarningText}</bee-card>
          </div>
        ` : nothing}
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th>Current (${this.request.target})</th>
                <th></th>
                <th>New (from ${sourceLabel})</th>
              </tr>
            </thead>
            <tbody>
              ${this._hasMultipleCategories
                ? this._groupedChanges().map((group) => html`
                    <tr class="category-header">
                      <td colspan="5">${group.label}</td>
                    </tr>
                    ${group.changes.map(
                      (c) => html`
                        <tr class=${this._deselected.has(c.mfe) ? "deselected" : ""}>
                          <td><bee-checkbox ?checked=${!this._deselected.has(c.mfe)} @bee-change=${() => this._toggleMfe(c.mfe)}></bee-checkbox></td>
                          <td>${c.mfe}</td>
                          <td class="mono">${c.currentTargetVersion || "(not present)"}</td>
                          <td class="arrow">-></td>
                          <td class="mono">${this._renderVersionCell(c)}</td>
                        </tr>
                      `
                    )}
                  `)
                : this.changes.map(
                    (c) => html`
                      <tr class=${this._deselected.has(c.mfe) ? "deselected" : ""}>
                        <td><bee-checkbox ?checked=${!this._deselected.has(c.mfe)} @bee-change=${() => this._toggleMfe(c.mfe)}></bee-checkbox></td>
                        <td>${c.mfe}</td>
                        <td class="mono">${c.currentTargetVersion || "(not present)"}</td>
                        <td class="arrow">-></td>
                        <td class="mono">${this._renderVersionCell(c)}</td>
                      </tr>
                    `
                  )}
            </tbody>
          </table>
        </div>
        ${this._error ? html`<bee-alert variant="error" open>
          <span slot="message">${this._error}</span>
        </bee-alert>` : ""}
        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._applying}></bee-button>
          <bee-button label=${this._applying ? "Creating PR..." : `${confirmLabel} (${this._selectedCount})`} bee-type=${confirmButtonType} @click=${this._confirm} ?disabled=${this._applying || this._selectedCount === 0} ?loading=${this._applying}></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
